import { motion } from "framer-motion";
import { 
  BookOpen, 
  Calculator, 
  Palette, 
  Globe,
  Music,
  Microscope,
  Monitor,
  Languages,
  Heart,
  Trophy
} from "lucide-react";

const academicLevels = [
  {
    id: "preprimary",
    level: "Pre-Primary",
    grades: "Nursery - UKG",
    ages: "2.5 - 6 years",
    icon: Palette,
    color: "bg-pink-50",
    iconColor: "text-pink-600",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80",
    description: "Our pre-primary program focuses on creating a strong foundation through play-based learning. We nurture young minds with age-appropriate activities that develop social, emotional, cognitive, and motor skills.",
    curriculum: [
      "Language Development",
      "Number Concepts",
      "Art & Craft",
      "Music & Movement",
      "Storytelling",
      "Physical Activities",
    ],
    approach: "Play-based learning with focus on social skills, creativity, and early literacy. Small class sizes ensure personalized attention.",
  },
  {
    id: "primary",
    level: "Primary School",
    grades: "Class 1 - 5",
    ages: "6 - 11 years",
    icon: BookOpen,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80",
    description: "Primary education at ID4U School builds strong fundamentals in core subjects while encouraging curiosity and a love for learning. Our activity-based approach makes learning engaging and effective.",
    curriculum: [
      "English Language",
      "Hindi / Regional Language",
      "Mathematics",
      "Environmental Studies",
      "General Knowledge",
      "Computer Basics",
      "Art & Craft",
      "Physical Education",
    ],
    approach: "Activity-based learning with hands-on experiments, projects, and field trips. Focus on building strong reading, writing, and mathematical skills.",
  },
  {
    id: "middle",
    level: "Middle School",
    grades: "Class 6 - 8",
    ages: "11 - 14 years",
    icon: Calculator,
    color: "bg-green-50",
    iconColor: "text-green-600",
    image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=800&q=80",
    description: "Middle school is a crucial transition period where students develop critical thinking and deeper subject understanding. We prepare students for higher education while fostering their individual interests.",
    curriculum: [
      "English",
      "Hindi / Sanskrit",
      "Mathematics",
      "Science",
      "Social Science",
      "Computer Science",
      "Third Language",
      "Physical Education",
    ],
    approach: "Concept-based learning with emphasis on analytical skills, research projects, and collaborative learning. Introduction to laboratory work and practical applications.",
  },
  {
    id: "high",
    level: "High School",
    grades: "Class 9 - 10",
    ages: "14 - 16 years",
    icon: Globe,
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?auto=format&fit=crop&w=800&q=80",
    description: "High school education prepares students for board examinations and future academic pursuits. Our comprehensive curriculum, combined with career counseling, helps students make informed decisions about their future.",
    curriculum: [
      "English",
      "Hindi / Sanskrit",
      "Mathematics",
      "Science (Physics, Chemistry, Biology)",
      "Social Science",
      "Information Technology",
      "Physical Education",
    ],
    approach: "Exam-oriented preparation with regular assessments, mock tests, and personalized coaching. Career guidance and counseling to help students choose their stream.",
  },
];

const extraCurricular = [
  { icon: Music, title: "Music & Dance", description: "Classical and contemporary music, dance classes" },
  { icon: Palette, title: "Art & Craft", description: "Drawing, painting, sculpture, and creative arts" },
  { icon: Trophy, title: "Sports", description: "Cricket, football, basketball, athletics, and more" },
  { icon: Monitor, title: "Technology", description: "Coding, robotics, and digital skills" },
  { icon: Languages, title: "Languages", description: "Additional language courses available" },
  { icon: Heart, title: "Life Skills", description: "Personal development and soft skills training" },
];

const Academics = () => {
  return (
    <div data-testid="academics-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Academics
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Comprehensive curriculum designed for holistic development at every stage
            </p>
          </motion.div>
        </div>
      </section>

      {/* Academic Levels */}
      {academicLevels.map((level, index) => (
        <section 
          key={level.id}
          id={level.id}
          className={`py-16 md:py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className={`inline-flex items-center gap-2 ${level.color} px-4 py-2 rounded-full mb-4`}>
                  <level.icon className={level.iconColor} size={20} />
                  <span className={`${level.iconColor} font-medium`}>{level.grades}</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {level.level}
                </h2>
                <p className="text-muted-foreground mb-6">Ages: {level.ages}</p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {level.description}
                </p>

                <h4 className="font-heading text-lg font-semibold text-gray-900 mb-3">
                  Curriculum Includes:
                </h4>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {level.curriculum.map((subject) => (
                    <div key={subject} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-2 h-2 bg-secondary rounded-full"></div>
                      <span>{subject}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-slate-100 p-4 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-2">Learning Approach:</h4>
                  <p className="text-sm text-gray-600">{level.approach}</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <div className="relative">
                  <img
                    src={level.image}
                    alt={level.level}
                    className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                  />
                  <div className={`absolute -bottom-4 -right-4 ${level.color} p-4 rounded-xl shadow-lg`}>
                    <level.icon className={`${level.iconColor} w-8 h-8`} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Extra-Curricular Activities */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Beyond Academics
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
              Extra-Curricular Activities
            </h2>
            <div className="w-16 h-1 bg-secondary mx-auto rounded-full"></div>
            <p className="text-white/80 mt-4 max-w-2xl mx-auto">
              We believe in nurturing all-round development through various activities
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {extraCurricular.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl"
              >
                <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center mb-4">
                  <activity.icon className="text-secondary" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  {activity.title}
                </h3>
                <p className="text-white/70 text-sm">{activity.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Academics;
